import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { User, Flavour } from '../types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  currentFlavourId: string | null;
  setCurrentFlavourId: (flavourId: string) => void;
  availableFlavours: Flavour[];
  refreshFlavours: () => Promise<void>;
  flavourChangeTrigger: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentFlavourId, setCurrentFlavourId] = useState<string | null>(null);
  const [availableFlavours, setAvailableFlavours] = useState<Flavour[]>([]);
  const [flavourChangeTrigger, setFlavourChangeTrigger] = useState(0);
  const navigate = useNavigate();

  // Check for existing token on app load
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const savedFlavourId = localStorage.getItem('currentFlavourId');

    if (token) {
      apiService.setAuthToken(token);
      if (savedFlavourId) {
        setCurrentFlavourId(savedFlavourId);
        apiService.setFlavourId(savedFlavourId);
      }
      // Validate token and fetch user data
      validateAndFetchUserData();
    } else {
      setIsLoading(false);
    }
  }, []);

  const validateAndFetchUserData = async () => {
    try {
      const isValid = await apiService.validateToken();
      if (isValid) {
        await fetchUserData();
      } else {
        logout();
      }
    } catch (error) {
      logout();
    }
  };

  const fetchUserData = async () => {
    try {
      const userData = await apiService.getCurrentUser();
      setUser(userData);

      // Fetch available flavours/apps for this user
      await refreshFlavours();
    } catch (error) {
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const refreshFlavours = async () => {
    try {
      const flavours = await apiService.getAvailableFlavours();
      setAvailableFlavours(flavours);

      if (flavours.length === 0) {
        return;
      }

      // Use the persisted selection as the source of truth here. We can't rely
      // on the `currentFlavourId` closure value because this runs right after a
      // page refresh, before the state update queued in the mount effect has
      // committed (a stale closure would always look "unset" and reset to the
      // first app). Reading localStorage avoids that race.
      const savedFlavourId = localStorage.getItem('currentFlavourId');
      const savedIsValid =
        !!savedFlavourId && flavours.some(f => f.id === savedFlavourId);

      if (savedIsValid) {
        // Keep the saved app; make sure state + API service stay in sync with it.
        setCurrentFlavourId(savedFlavourId);
        apiService.setFlavourId(savedFlavourId);
      } else {
        // No valid persisted selection (first login, or saved app no longer
        // available) — fall back to the first app.
        setCurrentFlavourId(flavours[0].id);
        apiService.setFlavourId(flavours[0].id);
        localStorage.setItem('currentFlavourId', flavours[0].id);
      }
    } catch (error) {
      // ignore flavour fetch failure
    }
  };

  const login = async (email: string, password: string) => {
    const response = await apiService.login(email, password);

    // Check if password setup is required
    if (response.success && response.data?.requiresPasswordSetup) {
      // Navigate to password setup page with admin data
      navigate('/set-password', {
        state: {
          adminId: response.data.adminId,
          email: response.data.email,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          role: response.data.role,
        }
      });
      return;
    }

    if (response.success && response.data?.authToken) {
      localStorage.setItem('authToken', response.data.authToken);
      apiService.setAuthToken(response.data.authToken);

      // Fetch complete user profile with flavourIds
      await fetchUserData();

      // Navigate to dashboard after successful login
      navigate('/');
    } else {
      throw new Error(response.message || 'Login failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentFlavourId');
    apiService.clearAuthToken();
    setUser(null);
    setCurrentFlavourId(null);
    setAvailableFlavours([]);
    navigate('/login');
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const handleSetCurrentFlavourId = (flavourId: string) => {
    setCurrentFlavourId(flavourId);
    apiService.setFlavourId(flavourId);
    localStorage.setItem('currentFlavourId', flavourId);

    // Trigger a refresh by incrementing the trigger counter
    setFlavourChangeTrigger(prev => prev + 1);
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    logout,
    updateUser,
    currentFlavourId,
    setCurrentFlavourId: handleSetCurrentFlavourId,
    availableFlavours,
    refreshFlavours,
    flavourChangeTrigger,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 